import { useState } from 'react';
import { DashLegend, GlowDonut, TrendLines } from '../../../shared/dashboardCharts';
import { IconFlash, IconPlus } from '../components/icons';
import { Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, StatCard, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { formatDate, formatNumber } from '../lib/format';
import { METER_CAPACITY_UNIT, METER_KIND_LABELS, METER_KIND_USES_CAPACITY, UTILITY_KINDS, type UtilityKind } from '../lib/utilityKinds';

/** Serienfarben je Sparte – Profilfarben vorn, damit das Farbprofil mitläuft. */
const KIND_COLORS: Record<UtilityKind, string> = {
  electricity: 'var(--warning)',
  water: 'var(--primary)',
  gas: 'var(--danger)',
  district_heat: 'var(--accent)',
  oil: 'var(--info)',
  pellets: 'var(--success)',
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Verbrauchsdatenerfassung (Objekt-Check, Kap. 7).
 *
 * Oben Kennzahlen und Grafiken, darunter die vollständige Historie aller
 * Erfassungen. Ohne Filter erscheint alles, was der Handelnde sehen darf –
 * Objekt, Sparte und Zeitraum schränken nur ein, wenn sie gewählt sind. Die
 * Erfassung selbst öffnet als Dialog: eine Zeile je aktivem Zähler eines
 * Objekts, ein gemeinsames Datum, ein Speichern-Vorgang für alle
 * ausgefüllten Zeilen.
 */
export function MeterReadingEntryPage() {
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);
  const [filters, setFilters] = useState({ propertyId: '', kind: '', from: '', to: '' });
  const [entryOpen, setEntryOpen] = useState(false);

  const history = useAsync(() => api.get('/api/properties/all/readings', filters), [JSON.stringify(filters)]);
  const rows: any[] = history.data ?? [];
  const filtered = Object.values(filters).some(Boolean);

  // Kennzahlen und Grafiken beschreiben genau die angezeigte Liste.
  const since30 = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);
  const last30 = rows.filter((row) => row.reading_date >= since30).length;
  const propertyCount = new Set(rows.map((row) => row.property_id)).size;
  const latest = rows[0]?.reading_date as string | undefined;
  const daysSince = latest ? Math.max(0, Math.round((Date.now() - new Date(latest + 'T00:00:00').getTime()) / 86_400_000)) : null;

  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - (11 - i));
    return d.toISOString().slice(0, 7);
  });
  const perMonth = months.map((month) => rows.filter((row) => String(row.reading_date).startsWith(month)).length);
  const perKind = UTILITY_KINDS.map((kind) => ({
    label: METER_KIND_LABELS[kind],
    value: rows.filter((row) => row.kind === kind).length,
    color: KIND_COLORS[kind],
  })).filter((entry) => entry.value > 0);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconFlash width={22} height={22} />}
        title="Verbrauchsdatenerfassung"
        subtitle="Zählerstände und Füllstände erfassen und die gesamte Historie aller Erfassungen einsehen."
        pill="Objekte"
        tone="info"
      />

      <div className="grid cols-4">
        <StatCard label="Erfassungen" value={rows.length} tone="primary" icon={<IconFlash width={17} height={17} />} hint={filtered ? 'im gewählten Filter' : 'gesamte Historie'} />
        <StatCard label="Letzte 30 Tage" value={last30} tone={last30 > 0 ? 'success' : 'warning'} hint="neue Erfassungen" />
        <StatCard label="Objekte" value={propertyCount} tone="accent" hint="mit Erfassungen" />
        <StatCard
          label="Letzte Erfassung"
          value={latest ? formatDate(latest) : '–'}
          tone={daysSince != null && daysSince > 45 ? 'warning' : 'info'}
          hint={daysSince == null ? 'noch nichts erfasst' : daysSince === 0 ? 'heute' : `vor ${daysSince} Tag${daysSince === 1 ? '' : 'en'}`}
        />
      </div>

      <div className="grid cols-2">
        <Card title="Erfassungen je Monat" subtitle="Letzte 12 Monate">
          <TrendLines ariaLabel={`Erfassungen je Monat: ${perMonth.join(', ')}`} series={[{ values: perMonth, color: 'var(--primary)' }]} />
          <div className="dash-legend-inline">
            <span>{months[0]} bis {months[11]}</span>
          </div>
        </Card>
        <Card title="Erfassungen je Sparte" subtitle="Anteil an der angezeigten Historie">
          {perKind.length ? (
            <div className="dash-pair">
              <GlowDonut segments={perKind} centerValue={String(rows.length)} centerLabel="Erfassungen" />
              <DashLegend items={perKind.map((entry) => ({ label: entry.label, value: String(entry.value), color: entry.color }))} />
            </div>
          ) : (
            <EmptyState title="Keine Erfassungen" />
          )}
        </Card>
      </div>

      <Card noBody>
        <div className="card-body tight">
          <div className="row" style={{ gap: 10 }}>
            <div style={{ minWidth: 220 }}>
              <SearchSelect
                value={filters.propertyId}
                onChange={(value) => setFilters({ ...filters, propertyId: value })}
                options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
                placeholder="Alle Objekte"
              />
            </div>
            <label className="toolbar-field">
              <span>Sparte</span>
              <select value={filters.kind} onChange={(event) => setFilters({ ...filters, kind: event.target.value })}>
                <option value="">alle</option>
                {UTILITY_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {METER_KIND_LABELS[kind]}
                  </option>
                ))}
              </select>
            </label>
            <label className="toolbar-field">
              <span>Von</span>
              <input type="date" value={filters.from} onChange={(event) => setFilters({ ...filters, from: event.target.value })} />
            </label>
            <label className="toolbar-field">
              <span>Bis</span>
              <input type="date" value={filters.to} onChange={(event) => setFilters({ ...filters, to: event.target.value })} />
            </label>
            {filtered && (
              <button className="btn sm ghost" onClick={() => setFilters({ propertyId: '', kind: '', from: '', to: '' })}>
                Filter zurücksetzen
              </button>
            )}
            <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setEntryOpen(true)}>
              <IconPlus width={15} height={15} /> Verbrauchsdaten erfassen
            </button>
          </div>
        </div>
      </Card>

      {history.loading && !history.data ? (
        <Loading />
      ) : history.error ? (
        <ErrorMessage error={history.error} />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconFlash width={20} height={20} />}
            title={filtered ? 'Keine Erfassung im gewählten Filter' : 'Noch keine Verbrauchsdaten erfasst'}
            hint={filtered ? undefined : 'Mit „Verbrauchsdaten erfassen“ den ersten Zählerstand eintragen.'}
          />
        </Card>
      ) : (
        <Card noBody>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Objekt</th>
                  <th>Sparte</th>
                  <th>Zähler</th>
                  <th className="num">Stand</th>
                  <th className="num">Veränderung</th>
                  <th>Notiz</th>
                  <th>Erfasst von</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const kind = row.kind as UtilityKind;
                  const unit = METER_CAPACITY_UNIT[kind];
                  const diff = row.previous_value != null ? row.reading_value - row.previous_value : null;
                  return (
                    <tr key={row.id}>
                      <td className="nowrap">{formatDate(row.reading_date)}</td>
                      <td>{row.property_name}</td>
                      <td>
                        <span className="dash-legend-inline" style={{ margin: 0 }}>
                          <span>
                            <i style={{ background: KIND_COLORS[kind] ?? 'var(--text-subtle)' }} />
                            {METER_KIND_LABELS[kind] ?? row.kind}
                          </span>
                        </span>
                      </td>
                      <td className="small subtle">
                        {METER_KIND_USES_CAPACITY[kind]
                          ? row.capacity != null
                            ? `von ${formatNumber(row.capacity, 2)} ${unit}`
                            : 'Kapazität offen'
                          : row.meter_number ?? '–'}
                      </td>
                      <td className="num">
                        {formatNumber(row.reading_value, 2)}
                        {unit ? ` ${unit}` : ''}
                      </td>
                      <td className="num small" style={{ color: diff == null ? undefined : diff < 0 ? 'var(--warning-text)' : undefined }}>
                        {diff == null ? '–' : `${diff > 0 ? '+' : ''}${formatNumber(diff, 2)}`}
                      </td>
                      <td className="small subtle">{row.note ?? ''}</td>
                      <td className="small subtle">
                        {[row.recorded_by_first_name, row.recorded_by_last_name].filter(Boolean).join(' ') || '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {entryOpen && (
        <EntryModal
          properties={properties.data ?? []}
          presetPropertyId={filters.propertyId}
          onClose={() => setEntryOpen(false)}
          onSaved={() => {
            setEntryOpen(false);
            history.reload();
          }}
        />
      )}
    </div>
  );
}

/** Erfassungsmaske: Objekt und Datum wählen, Stände je Zähler eintragen. */
function EntryModal({
  properties,
  presetPropertyId,
  onClose,
  onSaved,
}: {
  properties: any[];
  presetPropertyId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify } = useToast();
  const [propertyId, setPropertyId] = useState(presetPropertyId);
  const [readingDate, setReadingDate] = useState(today);
  const [values, setValues] = useState<Record<number, string>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const meters = useAsync(
    () => (propertyId ? api.get(`/api/properties/${propertyId}/meters`) : Promise.resolve([])),
    [propertyId],
  );
  const list: any[] = meters.data ?? [];
  const filled = list.filter((meter) => values[meter.id] !== undefined && values[meter.id] !== '');

  const save = async () => {
    if (filled.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/properties/${propertyId}/readings`, {
        readingDate,
        entries: filled.map((meter) => ({ meterId: meter.id, value: Number(values[meter.id]), note: notes[meter.id] || undefined })),
      });
      notify(`${filled.length} Ablesung(en) erfasst.`, 'success');
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Verbrauchsdaten erfassen"
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" disabled={busy || filled.length === 0} onClick={save}>
            {busy ? 'Wird erfasst …' : `Erfassen (${filled.length})`}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="form-grid">
          <Field label="Objekt" required>
            <SearchSelect
              value={propertyId}
              onChange={(value) => {
                setPropertyId(value);
                setValues({});
                setNotes({});
              }}
              options={properties.map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
              placeholder="– bitte wählen –"
            />
          </Field>
          <Field label="Erfassungsdatum" required>
            <input type="date" value={readingDate} max={today()} onChange={(event) => setReadingDate(event.target.value)} />
          </Field>
        </div>

        {propertyId &&
          (meters.loading ? (
            <Loading />
          ) : list.length === 0 ? (
            <EmptyState title="Keine Verbraucher für dieses Objekt" hint="Zähler werden im Register „Übersicht Verbraucher“ der Objektakte angelegt." />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Sparte</th>
                    <th>Kontext</th>
                    <th className="num">Letzter Stand</th>
                    <th style={{ width: 170 }}>Zählerstand / Füllstand</th>
                    <th>Notiz</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((meter: any) => {
                    const kind = meter.kind as UtilityKind;
                    const usesCapacity = METER_KIND_USES_CAPACITY[kind];
                    return (
                      <tr key={meter.id}>
                        <td>{METER_KIND_LABELS[kind] ?? meter.kind}</td>
                        <td className="small subtle">
                          {usesCapacity
                            ? meter.capacity != null
                              ? `von ${formatNumber(meter.capacity, 2)} ${METER_CAPACITY_UNIT[kind]}`
                              : 'Kapazität nicht hinterlegt'
                            : meter.meter_number ?? '–'}
                        </td>
                        <td className="num small subtle">
                          {meter.latest_reading_value != null ? `${formatNumber(meter.latest_reading_value, 2)} (${formatDate(meter.latest_reading_date)})` : '–'}
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={values[meter.id] ?? ''}
                            onChange={(event) => setValues((current) => ({ ...current, [meter.id]: event.target.value }))}
                            placeholder={usesCapacity ? METER_CAPACITY_UNIT[kind] : 'Stand'}
                          />
                        </td>
                        <td>
                          <input
                            value={notes[meter.id] ?? ''}
                            onChange={(event) => setNotes((current) => ({ ...current, [meter.id]: event.target.value }))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
